import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    type EnhancedToolResult,
    type ToolDefinition,
    type ToolExecutionContext,
} from '../../types';
import { DesignTool, type DesignToolArgs } from '../designTargeting';
import { checkpointDesign } from '../htmlAssetToolHelpers';
import { type ChartComposition, checkChartScene, type FrameSize } from './chartChecks';

import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

export const writeSceneHtmlSchema = z.object({
    html: z.string().describe('The complete <!DOCTYPE html>…</html> document for the scene.'),
});
export type WriteSceneHtmlArgs = z.infer<typeof writeSceneHtmlSchema>;

const NOT_LOADED_PROBLEM =
    "The scene couldn't be loaded to check it, so it may not show at all. Make sure it's a complete document whose scripts run without errors.";

// One retry: the second write that fails its checks ends the run.
const ATTEMPTS = 2;

/** Why a written scene can't be applied, and what to keep in the run's record about it. */
export interface SceneHtmlCheck {
    problem: string | null;
    record: Record<string, unknown> | null;
}

export type CheckSceneHtml = (
    composition: ChartComposition,
    frame: FrameSize,
) => Promise<SceneHtmlCheck>;

const checkChart: CheckSceneHtml = async (composition, frame) => {
    const { problem, layout } = await checkChartScene(composition, frame);
    return { problem, record: layout ? { layout } : null };
};

export interface WriteSceneHtmlToolArgs extends DesignToolArgs {
    /** The subagent's one scene: the tool writes nowhere else, so the subagent needs no scope. */
    sceneId: string;
    /** What a written scene must pass to be applied: a chart's checks unless given. */
    checkScene?: CheckSceneHtml;
    displayName?: string;
}

/**
 * The one tool of a subagent that designs a single scene (a chart, a layered graphic): applies the
 * scene's whole HTML, then checks it. A scene that fails its checks is refused with the reason and
 * keeps the design it had, so the designer can write it once more; anything else ends the run. What
 * the checks measure goes in the tool message's record, which no model is sent.
 */
export class WriteSceneHtmlTool extends DesignTool<
    typeof writeSceneHtmlSchema,
    WriteSceneHtmlToolArgs
> {
    private readonly sceneId: string;
    private readonly checkScene: CheckSceneHtml;
    private readonly displayName: string;
    // One tool per subagent run, so this counts that run's writes.
    private failedWrites = 0;

    public constructor(args: WriteSceneHtmlToolArgs) {
        super(args);
        this.sceneId = args.sceneId;
        this.checkScene = args.checkScene ?? checkChart;
        this.displayName = args.displayName ?? 'Write chart scene';
    }

    protected override readonly schema = writeSceneHtmlSchema;
    protected override readonly toolName = TOOL_NAMES.writeSceneHtml;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        "Replace the scene's whole HTML document with this one. It is checked; if it fails, you're " +
        'told why.';

    // Bound to its scene, so it takes no scene_id.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        return parameters;
    }

    public getToolDisplayName(): string {
        return this.displayName;
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: WriteSceneHtmlArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        // This tool is its scene's designer, so a graphic layered over the scene is its to write.
        const resolved = this.resolveTarget(this.sceneId);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset } = resolved;
        const previousHtml = asset.html;
        this.luminary.batchRun(() => {
            asset.html = args.html;
        });

        const { width, height } = this.luminary.referenceResolution;
        const check = await asset
            .introspectComposition((composition) => this.checkScene(composition, { width, height }))
            .then((checked) => checked ?? { problem: NOT_LOADED_PROBLEM, record: null })
            .catch(
                (error: unknown): SceneHtmlCheck => ({
                    problem: `${NOT_LOADED_PROBLEM} (${error instanceof Error ? error.message : 'unknown error'})`,
                    record: null,
                }),
            );
        if (check.problem) {
            this.luminary.batchRun(() => {
                asset.html = previousHtml;
            });
            this.failedWrites += 1;
            return {
                result: `Not applied: ${check.problem}`,
                isSuccess: false,
                ...(this.failedWrites >= ATTEMPTS ? { stopConversation: true } : {}),
                ...(check.record ? { record: check.record } : {}),
            };
        }
        checkpointDesign(this.luminary, this.dispatch, context.parentActionGroup);
        return {
            result: 'Applied.',
            isSuccess: true,
            stopConversation: true,
            ...(check.record ? { record: check.record } : {}),
        };
    }
}
